import { mapActions, mapGetters, mapMutations } from 'vuex'
import ClusterCardSmall from '@/components/ClusterCardSmall/ClusterCardSmall.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'
import HubCmsMapping from '@/components/HubCmsMapping/HubCmsMapping.vue'

export default {
  name: 'HubDetail',
  components: {
    ClusterCardSmall,
    ChatGPT,
    HubCmsMapping
  },
  data () {
    return {
      isLoading: {
        mappedArticles: false,
        unmappedArticles: false,
        topics: false,
        settings: false,
        addToMap: false,
        dismissArticle: false,
        keywordIdeas: false,
        initialLoad: false,
        hubBuilding: false,
        refresh: false
      },

      activeUnmappedTab: 0, // 0: All, 1: Articles, 2: Listicles, 3: New
      topicName: '',
      topicDescription: '',
      isSaving: false,
      formValid: true,
      topicClusters: [],
      mappedClustersList: [],
      clusterKeywords: {},
      selectedMetric: { text: 'Search Volume', value: 'estimated_search_volume' },
      clusterPage: 1,
      clusterPageLength: 30,
      funnelSections: [
        { id: 'top', name: 'Top Funnel' },
        { id: 'middle', name: 'Middle Funnel' },
        { id: 'bottom', name: 'Bottom Funnel' }
      ],
      settingsDialog: {
        show: false,
        name: '',
        description: '',
        loading: false
      },
      ideasDialog: {
        show: false,
        loading: false,
        suggestions: []
      },
      noTopicsAvailable: false,
      showCopilot: false
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'articles',
      'keywords',
      'activeContentplan',
      'activeWorkspace',
      'articlesTotalCount',
      'websocketMessages'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    topicId () {
      return parseInt(this.$route.params.topicId)
    },
    contentplanName () {
      return this.activeContentplan?.name
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    topicItems () {
      return this.topics?.map(topic => ({
        id: topic.id,
        name: topic.name
      }))
    },
    selectedTopic () {
      if (this.topicId === null || !this.topics?.length) return null
      return this.topics.find(topic => topic.id === this.topicId)
    },
    mappedArticles () {
      return this.mappedClustersList || []
    },
    mappedArticlesByFunnel () {
      return {
        top: this.mappedArticles.filter(article => article.funnel === 'top'),
        middle: this.mappedArticles.filter(article => article.funnel === 'middle'),
        bottom: this.mappedArticles.filter(article => article.funnel === 'bottom')
      }
    },
    unmappedArticles () {
      return this.articles || []
    },
    topicTotalCount () {
      return this.articlesTotalCount || 0
    },
    selectedTopicIdForRouter: {
      get () {
        return this.topicId
      },
      set (newTopicId) {
        if (newTopicId !== null && newTopicId !== this.topicId) {
          // Reset tab when topic changes
          this.activeUnmappedTab = 0
          this.$router.push({ name: 'HubDetail', params: { ...this.$route.params, topicId: newTopicId } })
        }
      }
    },
    showHubBuildingMessage () {
      return this.isLoading.hubBuilding
    }
  },
  methods: {
    ...mapActions([
      'articleGet',
      'topicGetList',
      'topicPut',
      'articlePut',
      'postHubExpand',
      'articlePost'
    ]),
    ...mapMutations([
      'setArticles',
      'setSnackbar',
      'removeArticle',
      'addArticle'
    ]),
    async refreshData () {
      await this.topicGetListData()
    },
    async topicGetListData () {
      if (!this.topicId) return
      try {
        await this.handleTopicChange(this.topicId)
      } catch (error) {
        console.error('Error loading topics:', error)
      }
    },
    async handleTopicChange (topicId) {
      if (!topicId) return
      this.isLoading.initialLoad = true
      try {
        this.mappedClustersList = []
        this.setArticles({ articles: [], total: 0 }) // Reset articles state

        const topic = this.topics.find(t => t.id === topicId)
        if (!topic) throw new Error(`Topic with ID ${topicId} not found in loaded topics.`)

        this.topicName = topic.name
        this.topicDescription = topic.description || ''
        document.title = `${topic.name} | ContentGecko`

        this.clusterPage = 1 // Reset page for the new topic
        this.activeUnmappedTab = 0 // Reset tab for the new topic
        await this.loadMappedArticles(topicId)
        await this.loadUnmappedArticles(topicId) // Load initial tab data
      } catch (error) {
        console.error(error)
      }
      this.isLoading.initialLoad = false
    },
    async loadMappedArticles (topicId) {
      this.isLoading.mappedArticles = true
      try {
        const response = await this.articleGet({
          contentplanId: this.contentplanId,
          filters: [
            { field: 'contentplanId', op: 'eq', value: this.contentplanId },
            { field: 'showInMap', op: 'eq', value: 1 },
            { field: 'topicId', op: 'eq', value: topicId }
          ],
          straightReturn: true
        })
        this.mappedClustersList = (response.items || []).map(article => ({
          ...article,
          funnel: ['top', 'middle', 'bottom'].includes(article.funnel) ? article.funnel : 'top'
        }))
      } catch (error) {
        console.error('Error loading mapped clusters:', error)
      } finally {
        this.isLoading.mappedArticles = false
      }
    },
    async loadUnmappedArticles (topicId) {
      this.isLoading.unmappedArticles = true
      try {
        const filters = [
          { field: 'contentplanId', op: 'eq', value: this.contentplanId },
          { field: 'topicId', op: 'eq', value: topicId },
          { field: 'showInMap', op: 'eq', value: 0 },
          { field: 'priority', op: 'in', value: ['not set', 'high'] }
        ]

        // Calculate date 7 days ago
        const date = new Date()
        date.setDate(date.getDate() - 7)
        const minCreatedAtDate = date.toISOString().split('T')[0]

        // Apply filters based on active tab
        switch (this.activeUnmappedTab) {
          case 0: // All
            // No extra filters needed, use defaults
            break
          case 1: // Articles
            filters.push({ field: 'type', op: 'eq', value: 'article' })
            break
          case 2: // Listicles & Buyer's Guides
            filters.push({ field: 'type', op: 'in', value: ['listicle', 'buyers-guide'] })
            break
          case 3: // Recently Added
            filters.push({ field: 'createdAt', op: 'gte', value: minCreatedAtDate })
            break
        }

        // If loading page 1, clear existing articles first
        if (this.clusterPage === 1) {
          this.setArticles({ articles: [], total: 0 })
        }

        await this.articleGet({
          contentplanId: this.contentplanId,
          filters,
          sort: [{ field: 'relevance', direction: 'desc' }],
          pagination: { limit: this.clusterPage * this.clusterPageLength, offset: 0 },
          includeKeywords: true
        })
      } catch (error) {
        console.error('Error loading unmapped clusters:', error)
      } finally {
        this.isLoading.unmappedArticles = false
      }
    },
    async loadNewClusters (isVisible) {
      if (isVisible && !this.isLoading.unmappedArticles && this.unmappedArticles.length < this.topicTotalCount && this.topicId !== null) {
        this.clusterPage++
        await this.loadUnmappedArticles(this.topicId)
      }
    },
    getArticleKeywords (articleId) {
      if (!this.keywords?.length) return []
      const tempKeywords = this.keywords
      return tempKeywords.filter(k => k.article_id === articleId)
    },
    openSettingsDialog () {
      const currentTopic = this.selectedTopic
      if (!currentTopic) return

      this.settingsDialog.name = this.topicName
      this.settingsDialog.description = this.topicDescription
      this.settingsDialog.show = true
    },
    async saveTopicSettings () {
      this.isLoading.settings = true
      try {
        const promises = []
        const nextName = (this.settingsDialog.name || '').trim()
        const nextDescription = this.settingsDialog.description || ''
        const currentName = this.selectedTopic?.name || ''
        const currentDescription = this.selectedTopic?.description || ''

        if (nextName && nextName !== currentName) {
          promises.push(this.topicPut({
            contentplanId: this.contentplanId,
            topicIds: [this.topicId],
            name: nextName,
            action: 'rename'
          }))
        }

        if (nextDescription !== currentDescription) {
          promises.push(this.topicPut({
            contentplanId: this.contentplanId,
            topicIds: [this.topicId],
            description: nextDescription,
            action: 'updateDescription'
          }))
        }

        if (!promises.length) {
          this.settingsDialog.show = false
          return
        }

        await Promise.all(promises)

        await this.topicGetList({ contentplanId: this.contentplanId })
        await this.topicGetListData()

        // Close the dialog
        this.settingsDialog.show = false
      } catch (error) {
        console.error('Error saving hub settings:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to save hub settings',
          type: 'error'
        })
      } finally {
        this.isLoading.settings = false
      }
    },
    async handleAddToMap (article, add = true) {
      if (!article) return
      const articleId = article.article_id
      if (!articleId) return

      this.isLoading.addToMap = articleId || true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: add ? 'addToMap' : 'removeFromMap',
          articleIds: [articleId]
        })
        if (add) {
          const mappedArticle = { ...article, funnel: article.funnel || 'top' }
          this.mappedClustersList.push(mappedArticle)
          this.removeArticle(articleId)
        } else {
          this.mappedClustersList = this.mappedClustersList.filter(a => a.article_id !== articleId)
          this.addArticle(article)
        }

        this.isLoading.addToMap = false
      } catch (e) {
        console.log(e)
        this.setSnackbar({ open: true, text: `Failed to ${add ? 'add' : 'remove'} article ${add ? 'to' : 'from'} favorites.`, type: 'error' })
        this.isLoading.addToMap = false
      }
    },
    async handleDismissArticle (article) {
      if (!article) return
      const articleId = article.article_id
      if (!articleId) return

      this.isLoading.dismissArticle = articleId

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updatePriority',
          articleIds: [articleId],
          priority: 'low'
        })

        this.removeArticle(articleId)
      } catch (error) {
        console.error('Error dismissing article:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to dismiss article',
          type: 'error'
        })
      } finally {
        this.isLoading.dismissArticle = false
      }
    },
    async openIdeasDialog () {
      if (this.topicId === null) return

      this.ideasDialog.show = true
      this.ideasDialog.suggestions = []
      this.ideasDialog.loading = true

      try {
        const result = await this.postHubExpand({
          contentplanId: this.contentplanId,
          topicId: this.topicId,
          runRelatedKeywords: false,
          saveKeywords: false
        })

        if (result && result.length > 0) {
          const topicSuggestions = result.find(item => item.topic_id === this.topicId)
          this.ideasDialog.suggestions = (topicSuggestions?.suggestedKeywords || []).map(s => ({
            text: s,
            loading: false,
            added: false
          }))
        }
      } catch (error) {
        console.error('Error loading keyword ideas:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to load keyword ideas',
          type: 'error'
        })
      } finally {
        this.ideasDialog.loading = false
      }
    },
    async createAndMapArticleFromSuggestion (suggestion) {
      if (!suggestion || suggestion.loading || suggestion.added) return

      suggestion.loading = true
      try {
        const newArticle = await this.articlePost({
          contentplanId: this.contentplanId,
          name: suggestion.text,
          topicId: this.topicId
        })

        if (newArticle && newArticle.article_id) {
          await this.handleAddToMap(newArticle, true)

          suggestion.added = true
        } else {
          throw new Error('Failed to create article from suggestion.')
        }
      } catch (error) {
        console.error('Error creating and mapping article from suggestion:', error)
        this.setSnackbar({
          open: true,
          text: `Failed to add '${suggestion.text}' to favorites.`,
          type: 'error'
        })
      } finally {
        suggestion.loading = false
      }
    },
    selectHub (topicId) {
      if (topicId !== null) {
        this.$router.push({ name: 'HubDetail', params: { ...this.$route.params, topicId } })
      }
    },
    handleWebsocketMessage (message) {
      if (!message) return
      console.log('message in HubDetail view', JSON.stringify(message))

      // Handle copilot messages
      if (message.messages?.[0]?.content?.stepName === 'Creating Hub' && message.messages?.[0]?.content?.status === 'done') {
        console.log('Creating Hub done')
        this.isLoading.hubBuilding = false
        this.loadUnmappedArticles(this.topicId)
        this.setSnackbar({
          open: true,
          text: 'Hub created successfully',
          type: 'success'
        })
      }
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    async refreshHubDetail () {
      this.isLoading.refresh = true
      try {
        if (this.topicId) {
          await this.handleTopicChange(this.topicId)
        } else {
          await this.topicGetList({ contentplanId: this.contentplanId })
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.refresh = false
      }
    }
  },
  watch: {
    async '$route' (to, from) {
      if (to.params.topicId !== from.params.topicId) {
        // Topic changed, reset tab and load data
        this.activeUnmappedTab = 0
        this.clusterPage = 1 // Reset page when topic changes
        await this.handleTopicChange(this.topicId)
      }
    },
    // Watcher for tab changes
    async activeUnmappedTab (newTab, oldTab) {
      if (newTab !== oldTab && this.topicId) {
        this.clusterPage = 1 // Reset page number
        await this.loadUnmappedArticles(this.topicId) // Load data for the new tab
      }
    }
  },
  created () {
    this.$store.subscribeAction({
      after: (action) => {
        if (action.type === 'handleWebsocketMessage' && action.payload) {
          this.handleWebsocketMessage(action.payload)
        }
      }
    })
  },
  async mounted () {
    this.isLoading.initialLoad = true
    try {
      if (this.$route.query?.newHub || this.$route.query?.isNewHub) {
        const { newHub, isNewHub, ...query } = this.$route.query
        this.$router.replace({ name: this.$route.name, params: this.$route.params, query })
      }

      // Always load topics first for the selector
      await this.topicGetList({ contentplanId: this.contentplanId })

      if (!this.topicId) {
        // No topicId in URL: Stop loading, show only selector and title.
        this.isLoading.initialLoad = false
        // Check if there are actually any topics to select
        if (!this.topics || this.topics.length === 0) {
          this.noTopicsAvailable = true
        }
        // Do not proceed further - wait for user to select a topic
        return
      }

      // TopicId is present in URL, proceed to load its data
      const topicExists = this.topics?.some(t => t.id === this.topicId)
      if (topicExists) {
        await this.handleTopicChange(this.topicId) // This already sets initialLoad = false
      } else {
        // Provided topicId is invalid.
        this.isLoading.initialLoad = false // Stop loading
        // Redirect to the first topic if available, otherwise show 'no topics'.
        if (this.topics && this.topics.length > 0) {
          const firstTopicId = this.topics[0].id
          this.$router.replace({ name: 'HubDetail', params: { ...this.$route.params, topicId: firstTopicId } })
          // The watcher will trigger handleTopicChange, which resets tab and loads data
        } else {
          this.noTopicsAvailable = true
        }
      }
    } catch (error) {
      console.error('Error during component mount:', error)
      this.noTopicsAvailable = true // Fallback to showing no topics on error
      this.isLoading.initialLoad = false
    }
  }
}
